/** /tours — cross-tenant Group_Tour catalog (grid). */
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PolicyIcon from '@mui/icons-material/Policy';
import { Box, Button, Chip, CircularProgress, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import * as toursApi from '@/api/tours';
import { TRIP_TYPE_LABELS } from '@/api/tours';
import type { TourListItem } from '@/api/tours';
import { deleteBatchAction, type BatchActionDef } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { formatDate, formatRelativeTime } from '@/utils/format';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { AiImportEntryButton } from '@/features/ai-import/AiImportEntryButton';
import { entityExportBatchAction } from '@/features/exports/EntityExportDialog';
import { easyGoLiveBatchRefusals } from '@/features/tour-operators/onboard/easyGoLiveRequired';

import { buildTourNamespaces } from './toursNamespaces';
import { ManageTagsDialog } from './ManageTagsDialog';
import { SetLaunchPolicyDialog } from './SetLaunchPolicyDialog';
import { TourCreateWizard } from './TourCreateWizard';

const passthrough = (v: string) => v || undefined;

/** The toolbar keeps the three dims QuerySearch can't subsume: tenant (mandated
 *  tree picker), the derived `time` bucket, and the tri-state `deleted`
 *  visibility toggle. trip_type / is_bespoke / use_as_template moved into the
 *  QuerySearch namespaces. */
const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'time', toApi: passthrough },
  { param: 'deleted', toApi: passthrough },
];

/** Presets save/restore the `?q=` DSL alongside the toolbar params. */
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS, ['q']);

const DEFAULT_ORDER = [
  'id',
  'tenant',
  'name',
  'trip_type',
  'is_bespoke',
  'start_date',
  'use_as_template',
  'num_bookings',
  'is_import_bound',
  'custom_id',
  'tags',
  'launch_policy',
  'start_datetime',
  'start_timezone',
  'end_date',
  'allow_selfreg',
  'has_departures',
  'created_ts',
  'updated_ts',
];

/** Columns present but hidden until toggled on via the column menu. */
const DEFAULT_HIDDEN = [
  'start_datetime',
  'start_timezone',
  'end_date',
  'allow_selfreg',
  'has_departures',
  'created_ts',
];

export function ToursListPage() {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const [wizardOpen, setWizardOpen] = useState(false);
  useCreateIntent(() => setWizardOpen(true));
  const presets = useGridPresets({
    grid: 'tours',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what New tour pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  // The single-tenant extras (suggestions, batch value pickers, AI import) act
  // only when the filter names exactly one operator (D5).
  const gridTenantId = singleTenantId(pageTenants.map((t) => t.id));
  const timeBucket = getParam('time') ?? 'all';
  const deleted = getParam('deleted') ?? 'exclude';

  // ── QuerySearch (`?q=` DSL → wire AST) ─────────────────────────────────────
  const namespaces = useMemo(() => buildTourNamespaces(), []);
  const q = presets.getParam('q') ?? '';
  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);
  const wire = useMemo(() => toWireAst(tree), [tree]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next); // divergence choke point (materializes a loaded preset)
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // The wire AST rides in `extra.filters` (a JSON string): `extra` is the only
  // channel ResourceDataGrid folds into BOTH the react-query key and the batch
  // `filterQuery`, so a query change refetches the list AND "select all matching"
  // applies to the filtered set — with zero ResourceDataGrid change.
  const gridExtra = useMemo(
    () => ({ ...extra, ...(wire.length ? { filters: JSON.stringify(wire) } : {}) }),
    [extra, wire],
  );
  // Launch-policy and tag suggestions come from one operator's picker endpoint.
  const extraFilterValues =
    gridTenantId !== null ? { tour_operator_id: String(gridTenantId) } : undefined;

  const columns = useMemo<GridColDef<TourListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/tours/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'trip_type',
        headerName: 'Kind',
        width: 110,
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            color={p.row.trip_type === 1 ? 'info' : p.row.trip_type === 2 ? 'default' : 'primary'}
            label={p.row.trip_type_display || TRIP_TYPE_LABELS[p.row.trip_type] || p.row.trip_type}
          />
        ),
      },
      {
        field: 'is_bespoke',
        headerName: 'Mode',
        width: 120,
        sortable: false,
        renderCell: (p) => (
          <Chip size="small" label={p.row.is_bespoke ? 'Fixed date' : 'Recurring'} />
        ),
      },
      {
        field: 'start_date',
        headerName: 'Start',
        width: 120,
        valueGetter: (_, row) => row.start_date,
        renderCell: (p) =>
          p.row.is_bespoke && p.row.start_date ? formatDate(p.row.start_date) : '—',
      },
      {
        field: 'use_as_template',
        headerName: 'Template',
        width: 100,
        sortable: false,
        renderCell: (p) => (p.row.use_as_template ? '✓' : '—'),
      },
      {
        field: 'num_bookings',
        headerName: 'Bookings',
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.num_bookings,
      },
      {
        field: 'is_import_bound',
        headerName: 'Import',
        width: 110,
        sortable: false,
        renderCell: (p) =>
          p.row.is_import_bound ? (
            <Chip size="small" variant="outlined" color="warning" label="Imported" />
          ) : (
            '—'
          ),
      },
      {
        field: 'custom_id',
        headerName: 'External ID',
        width: 150,
        sortable: false,
        renderCell: (p) =>
          p.row.custom_id ? (
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.row.custom_id}</span>
          ) : (
            '—'
          ),
      },
      {
        field: 'tags',
        headerName: 'Tags',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => (row.tags ?? []).map((t) => t.name ?? '').join(', '),
        renderCell: (p) => {
          const tags = p.row.tags ?? [];
          if (tags.length === 0) return '—';
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
              {tags.map((t) => (
                <Chip key={t.id} size="small" variant="outlined" label={t.name ?? `#${t.id}`} />
              ))}
            </Box>
          );
        },
      },
      {
        field: 'launch_policy',
        headerName: 'Launch policy',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.launch_policy?.name ?? '',
        renderCell: (p) => p.row.launch_policy?.name || '—',
      },
      {
        field: 'start_datetime',
        headerName: 'Start (datetime)',
        width: 170,
        // Not in the backend _ORDERING_MAP (unindexed on Group_Tour) — a sort would
        // silently fall back to name. Keep display-only.
        sortable: false,
        valueGetter: (_, row) => row.start_datetime,
        renderCell: (p) => (p.row.start_datetime ? formatDate(p.row.start_datetime) : '—'),
      },
      {
        field: 'start_timezone',
        headerName: 'Timezone',
        width: 150,
        sortable: false,
        renderCell: (p) => p.row.start_timezone || '—',
      },
      {
        field: 'end_date',
        headerName: 'End',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.end_date,
        renderCell: (p) => (p.row.end_date ? formatDate(p.row.end_date) : '—'),
      },
      {
        field: 'allow_selfreg',
        headerName: 'Self-reg',
        width: 100,
        sortable: false,
        renderCell: (p) => (p.row.allow_selfreg ? '✓' : '—'),
      },
      {
        field: 'has_departures',
        headerName: 'Has departures',
        width: 130,
        // Not in the backend _ORDERING_MAP — keep display-only.
        sortable: false,
        renderCell: (p) => (p.row.has_departures ? '✓' : '—'),
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 130,
        // created_ts IS in the backend _ORDERING_MAP — sortable.
        renderCell: (p) => formatRelativeTime(p.row.created_ts),
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 130,
        renderCell: (p) => formatRelativeTime(p.row.updated_ts),
      },
    ],
    [],
  );

  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'set_launch_policy',
        label: 'Set launch policy',
        icon: <PolicyIcon fontSize="small" />,
        permission: 'console_tours_manage',
        action: 'set_launch_policy',
        dialog: (ctx) => <SetLaunchPolicyDialog ctx={ctx} gridTenantId={gridTenantId} />,
        // A selection with tours of an easy tenant still under the outbound guard is refused
        // as a whole (409 easy_go_live_required): said with a Go live link per tenant.
        refusals: easyGoLiveBatchRefusals,
      },
      {
        key: 'manage_tags',
        label: 'Manage tags',
        icon: <LocalOfferIcon fontSize="small" />,
        permission: 'console_tours_manage',
        action: 'manage_tags',
        dialog: (ctx) => <ManageTagsDialog ctx={ctx} gridTenantId={gridTenantId} />,
      },
      entityExportBatchAction({
        permission: 'console_tours_export',
        entity: 'tour',
        resource: 'tours',
        noun: 'tour(s)',
      }),
      deleteBatchAction('console_tours_manage'),
    ],
    [gridTenantId],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      toursApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        // extra now carries tour_operator_id / time / deleted / filters (the wire AST).
        ...(params.extra as Record<string, never>),
        ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
      }),
    [orderingFallback],
  );

  const anyActive = anyFilterActive || q.trim().length > 0;
  const resetAll = useCallback(() => {
    resetAllFilters(); // clears tenant / time / deleted
    setQuery([]); // clears ?q=
  }, [resetAllFilters, setQuery]);

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'time',
      priority: 2,
      active: timeBucket !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="When"
          value={timeBucket}
          sx={{ width: 140 }}
          onChange={(e) => setParam('time', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">Any time</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="upcoming">Upcoming</MenuItem>
          <MenuItem value="past">Past</MenuItem>
          <MenuItem value="nonbespoke">Recurring</MenuItem>
        </TextField>
      ),
    },
    {
      key: 'deleted',
      priority: 0,
      active: deleted !== 'exclude',
      node: (
        <TextField
          select
          size="small"
          label="Deleted"
          value={deleted}
          sx={{ width: 130 }}
          onChange={(e) => setParam('deleted', e.target.value === 'exclude' ? null : e.target.value)}
        >
          <MenuItem value="exclude">Hide deleted</MenuItem>
          <MenuItem value="include">Include deleted</MenuItem>
          <MenuItem value="only">Only deleted</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="tours-grid-editor"
        title="Tours"
        subtitle="Cross-tenant Group_Tour catalog."
        actions={
          <>
            <AiImportEntryButton type="tour" tenantId={gridTenantId} />
            {hasPerm('console_tours_manage') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setWizardOpen(true)}
              >
                New tour
              </Button>
            )}
          </>
        }
      />

      {wizardOpen && (
        <TourCreateWizard open onClose={() => setWizardOpen(false)} pageTenants={pageTenants} />
      )}

      <ResponsiveFilterBar items={toolbarItems} />

      <Box sx={{ mb: 2 }}>
        <QuerySearchInput
          namespaces={namespaces}
          value={tree}
          onChange={setQuery}
          {...(extraFilterValues ? { extraFilterValues } : {})}
          placeholder="Search tours…  e.g. tag=~vip & trip_type=Tour & num_bookings=1+"
        />
      </Box>

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<TourListItem>
          queryKey={['tours']}
          queryFn={queryFn}
          allowExtendedTimeout
          columns={orderColumns(columns, presets.orderedFields)}
          extra={gridExtra}
          batchResource="tours"
          batchActions={batchActions}
          batchTenantFilterId={gridTenantId}
          emptyMessage="No tours yet."
          emptyAction={
            hasPerm('console_tours_manage') && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>
                New tour
              </Button>
            )
          }
          defaultSort={{ field: 'name', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAll,
            resetDisabled: !anyActive,
            exportFileName: 'tours',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
